import pcu from '@/pages/opinion/assets/svg/pcu.svg';
import starBlue from '@/pages/opinion/assets/svg/starblue.svg';
import sensor_tower from '@op/assets/svg/sensor_tower.svg';
import { countryCn, countryEn, langCn, langEn, NO_DATE, ratingColors, regionCn, regionEn } from '@op/constants';
import { ByTypeSelectType, MentionsSentimentTabType } from '../../../constants';
import { gameInfoType, ParamsTimeType } from '@op/types/common';
import { SymbolType } from '@op/constants/chart';
import { getChartsGradientColor } from '@op/utils/chart';
import { textShowElement } from '@op/components/ChartCommon/formatter';
import React from 'react';
import { formatFilterDate } from '@op/utils/time';
import {
  brandHealthFormat,
  decimalFormatPercentage,
  percentageFormat,
  reviewsFormat,
  sentimentFormat,
} from '@op/utils/value';
import { getPercentageInfo, mathFormat } from '@op/utils';
import moment from 'moment';
import { ServerTimeType } from '@op/components/FilterCommon/types';
import { getPositiveNegativeIconName } from '@op/pages/Performance/components/MentionsSentiment/chart';
import mixedIcon from '@op/assets/svg/rating_neutral.svg';
import positiveIcon from '@op/assets/svg/steam_positive.svg';
import negativeIcon from '@op/assets/svg/steam_negative.svg';
import { AnalysisSummaryTypeTab } from '@op/pages/AIAnalysisHub/KeywordAndURLAnalysis/types';

export type SentimentMentionsTwoLevelType =
  | 'mentions_brand_health'
  | 'mentions_sentiment'
  | 'mentions_DAU'
  | 'mentions_revenue'
  | 'mentions_download'
  | 'mentions_PCU'
  | 'mentions_digital_rev'
  | 'mentions_units'
  | 'mentions_engagement'
  | 'mentions_likes'
  | 'mobile_selected_avg_score'
  | 'mobile_lifetime_avg_score'
  | 'steam_filtered_positive'
  | 'steam_review_positive'
  | 'lifetime_negative'
  | 'lifetime_positive'
  | 'negative_mentions_ratio';

function addIcon(icon: any, text: string, formatMessage: any) {
  return (
    <div className="dbo-inlineflex">
      {formatMessage({ id: 'Mentions' })} &{' '}
      <img
        src={icon}
        className={'svg-icon'}
        style={{ marginRight: '4px', marginLeft: '4px' }}
      />
      {formatMessage({ id: text })}
    </div>
  );
}

const nameToMap: { [key in ServerTimeType]: string } = {
  daily: 'DAU',
  hourly: 'DAU',
  weekly: 'WAU',
  monthly: 'MAU',
};
export const sentimentMentionsMobileTowLevelOptions = (
  formatMessage: any,
  filter: { date_type: ServerTimeType },
) => {
  const name = nameToMap[filter.date_type || 'daily'];

  return [
    {
      text: formatMessage({ id: 'Mentions & Brand Health' }),
      value: 'mentions_brand_health',
      rightTitle: formatMessage({ id: 'Brand Health' }),
    },
    {
      text: formatMessage({ id: 'Mentions & Sentiment' }),
      value: 'mentions_sentiment',
      rightTitle: formatMessage({ id: 'Sentiment' }),
    },
    {
      text: formatMessage({ id: 'Mentions & Engagement' }),
      value: 'mentions_engagement',
      rightTitle: formatMessage({ id: 'Engagement' }),
    },
    {
      text: formatMessage({ id: 'Mentions & Likes' }),
      value: 'mentions_likes',
      rightTitle: formatMessage({ id: 'Likes' }),
    },
    {
      text: formatMessage({ id: 'Mentions & Negative%' }),
      value: 'negative_mentions_ratio',
      rightTitle: formatMessage({ id: 'Negative%-2' }),
    },
    {
      text: addIcon(sensor_tower, name, formatMessage),
      value: 'mentions_DAU',
      rightTitle: formatMessage({ id: name }),
    },
    {
      text: addIcon(sensor_tower, 'Revenue', formatMessage),
      value: 'mentions_revenue',
      rightTitle: formatMessage({ id: 'Revenue' }),
    },
    {
      text: addIcon(sensor_tower, 'Download', formatMessage),
      value: 'mentions_download',
      rightTitle: formatMessage({ id: 'Download' }),
    },
  ];
};

export const sentimentMentionsPCTowLevelOptions = (formatMessage: any) => [
  {
    text: formatMessage({ id: 'Mentions & Brand Health' }),
    value: 'mentions_brand_health',
    rightTitle: formatMessage({ id: 'Brand Health' }),
  },
  {
    text: formatMessage({ id: 'Mentions & Sentiment' }),
    value: 'mentions_sentiment',
    rightTitle: formatMessage({ id: 'Sentiment' }),
  },
  {
    text: formatMessage({ id: 'Mentions & Engagement' }),
    value: 'mentions_engagement',
    rightTitle: formatMessage({ id: 'Engagement' }),
  },
  {
    text: formatMessage({ id: 'Mentions & Likes' }),
    value: 'mentions_likes',
    rightTitle: formatMessage({ id: 'Likes' }),
  },
  {
    text: formatMessage({ id: 'Mentions & Negative%' }),
    value: 'negative_mentions_ratio',
    rightTitle: formatMessage({ id: 'Negative%-2' }),
  },
  {
    text: addIcon(pcu, 'PCU', formatMessage),
    value: 'mentions_PCU',
    rightTitle: formatMessage({ id: 'PCU' }),
  },
  {
    text: addIcon(pcu, 'Digital Revenue', formatMessage),
    value: 'mentions_digital_rev',
    rightTitle: formatMessage({ id: 'Digital Revenue' }),
  },
  {
    text: addIcon(pcu, 'Units', formatMessage),
    value: 'mentions_units',
    rightTitle: formatMessage({ id: 'Units' }),
  },
];

type KeyType =
  | 'dau'
  | 'revenue'
  | 'download'
  | 'digital_rev'
  | 'units'
  | 'pcu'
  | 'avg_sentiment'
  | 'brand_health'
  | 'game_store_reviews'
  | 'videos'
  | 'posts'
  | 'exclude_discord_comments'
  | 'discord_mentions'
  | 'positive_mentions'
  | 'neutral_mentions'
  | 'negative_mentions'
  | 'high_mentions'
  | 'medium_mentions'
  | 'low_mentions'
  | 'invalid_mentions'
  | 'mentions'
  | 'mobile_rating_1_mentions'
  | 'mobile_rating_2_mentions'
  | 'mobile_rating_3_mentions'
  | 'mobile_rating_4_mentions'
  | 'mobile_rating_5_mentions'
  | 'positive'
  | 'mixed_rating'
  | 'negative'
  | 'likes'
  | 'engagement'
  | 'mobile_selected_avg_score'
  | 'mobile_lifetime_avg_score'
  | 'steam_filtered_positive'
  | 'steam_review_positive'
  | 'negative_mentions_ratio';

export type ChartOptionsMapType = Array<{
  name: string;
  key: KeyType;
  color: string;
  tab?: MentionsSentimentTabType;
  gameTypes?: Array<'pc' | 'console' | 'mobile'>;
  tips?: string;
  isStar?: boolean;
  exclude?: boolean;
  format?: (value: any) => string;
}>;

const visualMap = [
  {
    show: false,
    type: 'piecewise',
    seriesIndex: 0,
    pieces: [
      { gte: -102, lt: 0, color: '#CC5E6B' },
      { gte: 0, lte: 102, color: '#4DAB7D' },
    ],
  },
];

function getStarOptions(extra = {}): ChartOptionsMapType {
  return [
    {
      tips: '5星',
      name: '5',
      key: 'mobile_rating_5_mentions',
      isStar: true,
      gameTypes: ['mobile'],
      color: ratingColors[4],
      ...extra,
    },
    {
      tips: '4星',
      name: '4',
      key: 'mobile_rating_4_mentions',
      isStar: true,
      gameTypes: ['mobile'],
      color: ratingColors[3],
      ...extra,
    },
    {
      tips: '3星',
      name: '3',
      key: 'mobile_rating_3_mentions',
      isStar: true,
      gameTypes: ['mobile'],
      color: ratingColors[2],
      ...extra,
    },
    {
      tips: '2星',
      name: '2',
      key: 'mobile_rating_2_mentions',
      isStar: true,
      gameTypes: ['mobile'],
      color: ratingColors[1],
      ...extra,
    },
    {
      tips: '1星',
      name: '1',
      key: 'mobile_rating_1_mentions',
      isStar: true,
      gameTypes: ['mobile'],
      color: ratingColors[0],
      ...extra,
    },
  ];
}

const options: {
  [key in ByTypeSelectType]: ChartOptionsMapType;
} = {
  by_mentions_type: [
    {
      name: 'Game Store Reviews',
      color: '#6E7794',
      key: 'game_store_reviews',
      tab: 'total',
    },
    { name: 'Videos', color: '#F8D74D', key: 'videos' },
    { name: 'Posts-2', color: '#7F6FF1', key: 'posts' },
    { name: 'Comments-1', color: '#5EC8F1', key: 'exclude_discord_comments' },
    { name: 'Discord Chats', color: '#AD53D4', key: 'discord_mentions' },
  ],
  by_sentiment: [
    { name: 'Positive', color: '#69D19C', key: 'positive_mentions' },
    { name: 'Neutral', color: '#6E7794', key: 'neutral_mentions' },
    { name: 'Negative', color: '#F17C86', key: 'negative_mentions' },
  ],
  by_validation: [
    { name: 'High', color: '#435DC8', key: 'high_mentions' },
    { name: 'Medium', color: '#7CADFC', key: 'medium_mentions' },
    { name: 'Low', color: '#A6C9FD', key: 'low_mentions' },
    { name: 'Invalid', color: '#D0E4FE', key: 'invalid_mentions' },
  ],
  exclude_neutral: [
    { name: 'Positive', color: '#69D19C', key: 'positive_mentions' },
    { name: 'Negative', color: '#F17C86', key: 'negative_mentions' },
  ],
  exclude_low_invalid: [
    { name: 'High', color: '#435DC8', key: 'high_mentions' },
    { name: 'Medium', color: '#7CADFC', key: 'medium_mentions' },
  ],
  by_rating_ratio: [
    {
      name: 'Negative%',
      color: '#F17C86',
      key: 'negative',
      gameTypes: ['pc', 'console'],
    },
    {
      name: 'Mixed%',
      color: '#6E7794',
      key: 'mixed_rating',
      gameTypes: ['pc', 'console'],
    },
    {
      name: 'Positive%',
      color: '#69D19C',
      key: 'positive',
      gameTypes: ['pc', 'console'],
    },
    ...getStarOptions({
      format: decimalFormatPercentage,
    }),
  ],
  by_rating: [
    {
      name: 'Negative',
      color: '#F17C86',
      key: 'negative',
      gameTypes: ['pc', 'console'],
    },
    {
      name: 'Mixed',
      color: '#6E7794',
      key: 'mixed_rating',
      gameTypes: ['pc', 'console'],
    },
    {
      name: 'Positive',
      color: '#69D19C',
      key: 'positive',
      gameTypes: ['pc', 'console'],
    },
    ...getStarOptions(),
  ],
  in_total: [{ name: 'Mentions', color: '#4F86F3', key: 'mentions' }],
};

const getDefaultMetricsList: (dateType?: ServerTimeType) => Array<{
  key: SentimentMentionsTwoLevelType;
  name: string;
  valueKey?: string;
  color?: string;
  icon?: string;
  itemStyle?: any;
  numFmt?: Array<any>;
}> = (dateType = 'daily') => {
  const dauName = nameToMap[dateType];
  return [
    {
      key: 'mentions_brand_health',
      name: 'Brand Health',
      icon: 'path://M0 0 L12 0 L12 2 L0 2 Z',
      itemStyle: {
        color: {
          type: 'linear',
          colorStops: [
            { offset: 0, color: '#CC5E6B' }, // 0% 处的颜色
            { offset: 0.5, color: '#CC5E6B' }, // 50% 处的颜色，与前一个相同
            { offset: 0.5, color: '#4DAB7D' }, // 50% 处的颜色开始变化
            { offset: 1, color: '#4DAB7D' }, // 100% 处的颜色
          ],
          global: false,
        },
      },
    },
    {
      key: 'mentions_sentiment',
      name: 'Sentiment',
    },
    {
      key: 'mentions_engagement',
      name: 'Engagement',
    },
    {
      key: 'mentions_likes',
      name: 'Likes',
    },
    {
      key: 'negative_mentions_ratio',
      name: 'Negative%-2',
      itemStyle: {
        color: '#CC5E6B'
      },
      color: '#CC5E6B'
    },
    { key: 'mentions_DAU', name: dauName },
    { key: 'mentions_revenue', name: 'Revenue' },
    { key: 'mentions_download', name: 'Download' },
    { key: 'mentions_PCU', name: 'PCU' },
    { key: 'mentions_digital_rev', name: 'Digital REV' },
    { key: 'mentions_units', name: 'Units' },

    {
      key: 'mobile_selected_avg_score',
      name: 'Filtered Avg. Rating',
      color: '#5086F3',
    },
    {
      key: 'mobile_lifetime_avg_score',
      name: 'Avg. Rating of Game Store Reviews',
      color: '#A6C9FF',
    },
    {
      key: 'steam_filtered_positive',
      name: 'Filtered Positive%',
      color: '#4EAB7D',
    },
    {
      key: 'steam_review_positive',
      name: 'Steam Review Positive%',
      color: '#69D19C',
    },
  ];
};

const getDefaultMetricsChartOptions: (dateType?: ServerTimeType) => {
  [key in SentimentMentionsTwoLevelType]?: {
    series: {
      name: string;
      icon?: string;
      itemStyle?: any;
      type?: 'line' | 'bar';
      yAxisIndex?: number;
    };
    legends: {
      name: string;
      icon?: string;
      itemStyle?: any;
    };
  };
} = (dateType = 'daily') => {
  return getDefaultMetricsList(dateType).reduce(
    (pre, { name, color = '#326FF0', key, ...other }) => {
      return {
        ...pre,
        [key]: {
          series: {
            name: name,
            type: 'line',
            yAxisIndex: 1,
            itemStyle: { color: color },
          },
          legends: {
            name: name,
            icon: 'path://M0 0 L12 0 L12 2 L0 2 Z',
            itemStyle: { color: color },
            ...other,
          },
        },
      };
    },
    {},
  );
};

export const richImgMapFunc = () => {
  return [
    { key: 'dau', icon: sensor_tower },
    { key: 'pcu', icon: pcu },
    { key: 'star', icon: starBlue, width: 12, height: 12 },
    { key: 'positive', icon: positiveIcon },
    { key: 'negative', icon: negativeIcon },
    { key: 'mixed', icon: mixedIcon },
  ].reduce((pre: any, { key, icon, ...other }) => {
    return {
      ...pre,
      [key]: {
        height: 16,
        width: 16,
        backgroundColor: { image: icon },
        ...other,
      },
    };
  }, {});
};

export const starIconList = ['1', '2', '3', '4', '5'];
export const dauIconList = ['DAU', 'Revenue', 'Download', 'WAU', 'MAU'];
export const pcuIconList = ['PCU', 'Digital REV', 'Units'];
const sentimentBrandHealth = ['Sentiment', 'Brand Health'];
const gameStoreReviewRating = ['Avg. Rating of Game Store Reviews'];
const filterRating = ['Filtered Avg. Rating'];
const engagementLikes = ['Engagement', 'Likes'];
const steamFilteredPositive = ['Filtered Positive%'];
const steamReviewPositive = ['Steam Review Positive%'];
export const steamPositiveNegative = [
  'Positive',
  'Negative',
  'Positive%',
  'Negative%',
];
export const mentionsNegativeRatio = ['Negative%-2'];
export const mixedRating = ['Mixed', 'Mixed%'];
export const allSpecialKeyMetrics = [
  ...dauIconList,
  ...pcuIconList,
  ...sentimentBrandHealth,
  ...engagementLikes,
  ...gameStoreReviewRating,
  ...filterRating,
  ...steamFilteredPositive,
  ...steamReviewPositive,
  ...mentionsNegativeRatio
];
export const sentimentBrandHealthFormat: any = {
  Sentiment: sentimentFormat,
  'Brand Health': brandHealthFormat,
  'Filtered Positive%': percentageFormat,
  'Steam Review Positive%': percentageFormat,
  'Negative%-2': (val: number) => percentageFormat(val * 100),
};

export const textShowFun = (formatMessage: any, options: any = {}) => {
  const { gameStoreShowMarker = false, byType } = options;

  const formatToClassname = (seriesName: string) =>
    seriesName
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, '-');

  return (marker: string, seriesName: string, data?: any) => {
    if (starIconList.includes(seriesName)) {
      return textShowElement(
        marker,
        `<span class="tooltip-star tooltip-star-${seriesName}"></span>`,
      );
    }
    const text = formatMessage({ id: seriesName });
    if (sentimentBrandHealth.includes(seriesName)) {
      const className =
        seriesName === 'Sentiment' ? 'sentiment' : 'brand-health';
      return textShowElement('', text, className);
    }

    if (engagementLikes.includes(seriesName)) {
      return textShowElement('', text, 'engagement-likes');
    }

    if (gameStoreReviewRating.includes(seriesName) && !gameStoreShowMarker) {
      return textShowElement('', text, 'game-store-reviews-rating');
    }

    if (filterRating.includes(seriesName) && !gameStoreShowMarker) {
      return textShowElement('', text, 'filtered-rating');
    }

    if (['by_rating', 'by_rating_ratio']?.includes(byType)) {
      if (steamPositiveNegative.includes(seriesName)) {
        const info = getPositiveNegativeIconName(
          seriesName,
          formatMessage,
          seriesName,
          `${formatToClassname(seriesName)}`,
        );
        return textShowElement(marker, info, formatToClassname(seriesName));
      }

      if (mixedRating.includes(seriesName)) {
        const mixedText = `<span><img src=${mixedIcon} style="width: 18px;height: 18px; vertical-align: middle;margin-right:4px" />${formatMessage(
          { id: seriesName },
        )}</span>`;
        return textShowElement(
          marker,
          mixedText,
          formatToClassname(seriesName),
        );
      }
    }

    if (
      [...steamFilteredPositive, ...steamReviewPositive].includes(seriesName)
    ) {
      const info = getPositiveNegativeIconName(
        seriesName,
        formatMessage,
        seriesName,
        `${formatToClassname(seriesName)}`,
      );
      return textShowElement(marker, info, formatToClassname(seriesName));
    }

    if ([...pcuIconList, ...dauIconList].includes(seriesName)) {
      return textShowElement(
        '',
        text,
        `pcu-dau ${dauIconList.includes(seriesName) ? 'dau' : 'pcu'}`,
      );
    }

    if (mentionsNegativeRatio.includes(seriesName)) {
      return textShowElement('', text, 'mentions-negative-ratio');
    }

    return textShowElement(marker, text);
  };
};

export const labelFormat =
  (formatMessage: any, total: number) => (params: any) => {
    const percent = total
      ? getPercentageInfo(params?.value, total, 2)
      : `${params.percent.toFixed(2)}%`;
    if (starIconList.includes(params.name)) {
      let star = '';
      for (let i = 0; i < params.name; i++) {
        star += `{star|}`;
      }
      return `${star}: {a|${mathFormat(
        params.value,
      )}(${percent})}\n{hr|}\n{a|}`;
    }
    return `{value| ${formatMessage({
      id: params.name || NO_DATE.EMPTY,
    })}}: {a|${mathFormat(params.value)}(${percent})}\n{hr|}\n{a|}`;
  };

function getDefaultChartData({
  // value,
  key,
  hideMetricsSelect,
  dateType,
  getDataInfo,
}: {
  key: SentimentMentionsTwoLevelType | SentimentMentionsTwoLevelType[];
  hideMetricsSelect: boolean;
  dateType?: ServerTimeType;
  getDataInfo: (key: KeyType) => any[];
}): { series: Array<any>; legend: { data: Array<any>;[key: string]: any } } {
  if (hideMetricsSelect) return { series: [], legend: { data: [] } };
  const metricsArray = Array.isArray(key) ? key : [key];

  const seriesArr: any = [];
  const legendArr: any = [];
  metricsArray.forEach((key) => {
    const valueKey = mentionsKeyMetricsValueKeyMap?.[key];
    if (!valueKey) return;
    const value = getDataInfo(valueKey);
    if (!value) return;
    const defaultValue = getDefaultMetricsChartOptions(dateType);
    const { series, legends } = defaultValue[key] || {};
    seriesArr.push({
      ...series,
      data: value.map((val) =>
        valueKey === 'brand_health' ? val : val || null,
      ),
    });
    legendArr.push(legends);
  });

  return {
    series: seriesArr, // 不连接0数据
    legend: { data: legendArr },
  };
}

export const mentionsKeyMetricsValueKeyMap: {
  [key in SentimentMentionsTwoLevelType]: KeyType | undefined;
} = {
  mentions_brand_health: 'brand_health',
  mentions_sentiment: 'avg_sentiment',
  mentions_DAU: 'dau',
  mentions_revenue: 'revenue',
  mentions_download: 'download',
  mentions_PCU: 'pcu',
  mentions_digital_rev: 'revenue',
  mentions_units: 'units',
  mentions_engagement: 'engagement',
  mentions_likes: 'likes',
  mobile_selected_avg_score: 'mobile_selected_avg_score',
  mobile_lifetime_avg_score: 'mobile_lifetime_avg_score',
  steam_filtered_positive: 'steam_filtered_positive',
  steam_review_positive: 'steam_review_positive',
  lifetime_positive: undefined,
  lifetime_negative: undefined,
  negative_mentions_ratio: 'negative_mentions_ratio'
};

export function getRenderList({
  currentTab,
  gameInfo,
  by_type,
  key_metrics,
  need_all_list,
  filter,
}: {
  currentTab: MentionsSentimentTabType | AnalysisSummaryTypeTab;
  gameInfo: gameInfoType;
  by_type: ByTypeSelectType;
  key_metrics: SentimentMentionsTwoLevelType | SentimentMentionsTwoLevelType[];
  need_all_list?: boolean;
  filter?: any;
}) {
  const gameType = gameInfo.entity_type;
  const channelInfo = filter?.channel_comments || filter?.channel;

  let list = options[by_type].filter(({ tab, gameTypes }) => {
    const isCurrentTab = !tab || currentTab === tab;
    const isCurrentGameType = !gameTypes || gameTypes.includes(gameType as any);

    return isCurrentTab && isCurrentGameType;
  });

  if (
    ['by_rating_ratio', 'by_rating'].includes(by_type) &&
    (currentTab === 'game_store' || currentTab === 'steam') &&
    gameType === 'pc' &&
    Array.isArray(channelInfo) &&
    channelInfo.length === 1 &&
    channelInfo[0] === 'steam'
  ) {
    list = list.filter((val) => val.key !== 'mixed_rating');
  }

  if (need_all_list) {
    const metricsArray = Array.isArray(key_metrics)
      ? key_metrics
      : [key_metrics];
    metricsArray.forEach((each) => {
      const metric = mentionsKeyMetricsValueKeyMap?.[each];
      if (metric) {
        list.unshift({
          key: metric, // 添加类型断言
          name: metric,
          color: '',
          exclude: true,
        });
      }
    });
  }
  return list;
}

export function getChartsOptions({
  currentTab,
  gameInfo,
  by_type,
  data,
  metrics,
  hideMetricsSelect,
  type,
  chartRealType,
  showTotal,
  dateType,
  filter,
}: {
  currentTab: MentionsSentimentTabType | AnalysisSummaryTypeTab;
  gameInfo: gameInfoType;
  by_type: ByTypeSelectType;
  data: { [key in KeyType]?: Array<any> };
  metrics: SentimentMentionsTwoLevelType | SentimentMentionsTwoLevelType[];
  hideMetricsSelect: boolean;
  type: SymbolType;
  chartRealType: 'bar' | 'line';
  showTotal?: boolean;
  dateType: ParamsTimeType;
  filter: any;
}) {
  function getDataInfo(key: KeyType) {
    return data?.[key] || [];
  }

  const metricsArray = Array.isArray(metrics) ? metrics : [metrics];

  const list = getRenderList({
    currentTab,
    gameInfo,
    by_type,
    key_metrics: metricsArray,
    filter,
  });

  const { series, legend } = getDefaultChartData({
    key: metricsArray,
    hideMetricsSelect,
    dateType,
    getDataInfo,
  });

  const totalList: Array<number> = [];
  list.forEach(({ name, key, color, ...others }) => {
    const item: any = {
      name: name,
      type: chartRealType,
      itemStyle: { color },
      data: getDataInfo(key),
      ...others,
    };
    if (['bar', 'stack_line'].includes(type)) {
      item.stack = 'Total';
      if (type === 'bar') {
        getDataInfo(key).forEach((val, index) => {
          if (totalList[index] === undefined) {
            totalList[index] = val;
          } else {
            totalList[index] += val;
          }
        });
      }
      if (type === 'stack_line') {
        item.areaStyle = getChartsGradientColor(color, 0, 0.4);
      }
    }
    series.push(item);

    legend.data.push({
      name: name,
      itemStyle: { color },
    });
  });

  if (type === 'bar' && !showTotal) {
    series.push({
      name: 'Total',
      type: 'bar',
      stack: 'Total',
      data: totalList.map(() => 0),
      label: {
        show: true,
        position: 'top',
        formatter: (params: any) =>
          totalList[params.dataIndex]
            ? reviewsFormat(totalList[params.dataIndex])
            : '',
      },
      itemStyle: { color: 'rgba(0,0,0,0)' },
    });
  }

  // 添加一个计算位置的数据进去
  series.push({
    name: 'TopTipsEvent',
    type: chartRealType,
    stack: 'Total',
    data: totalList.map(() => 0),
  });

  const result: { [key: string]: any } = {
    series,
    legend,
  };

  if (metrics?.includes('steam_review_positive')) {
    legend.selected = {
      'Steam Review Positive%': false,
    };
  }

  if (!hideMetricsSelect && metricsArray.includes('mentions_brand_health')) {
    result.visualMap = visualMap;
  }

  return result;
}

const getDefaultColumns: (dateType?: ServerTimeType) => {
  [key in SentimentMentionsTwoLevelType]?: {
    name: string;
    key: string;
  };
} = (dateType = 'daily') => {
  return getDefaultMetricsList(dateType).reduce((pre, { name, key }) => {
    const valueKey = mentionsKeyMetricsValueKeyMap?.[key];
    if (!valueKey) return pre;
    const format = sentimentBrandHealthFormat[name];
    return { ...pre, [key]: { name, key: valueKey, format } };
  }, {});
};

function getDefaultTableColumns(
  key: SentimentMentionsTwoLevelType | SentimentMentionsTwoLevelType[],
  filter: any,
  needTotal = true,
): Array<{
  name: string;
  key: string;
  align?: 'right' | 'left';
  format?: (value: any) => string | number;
}> {
  const result: any = [
    {
      key: 'time',
      name: 'Date',
      align: 'left',
      render: (cvm: any) => {
        return formatFilterDate(cvm.time, filter);
      },
    },
  ];
  const metricsArr: SentimentMentionsTwoLevelType[] = Array.isArray(key)
    ? key
    : [key];
  metricsArr.forEach((key) => {
    const value = getDefaultColumns(filter.date_type);
    const item = value[key];
    if (item) result.push(item);
  });

  if (needTotal) result.push({ key: 'total', name: 'Mentions' });

  return result;
}

export function getTableColumns({
  currentTab,
  gameInfo,
  by_type,
  metrics,
  filter,
}: {
  currentTab: MentionsSentimentTabType;
  gameInfo: gameInfoType;
  by_type: ByTypeSelectType;
  metrics: SentimentMentionsTwoLevelType | SentimentMentionsTwoLevelType[];
  filter: any;
}) {
  const defaultColumns = getDefaultTableColumns(
    metrics,
    filter,
    by_type !== 'in_total',
  );

  const list = getRenderList({
    currentTab,
    gameInfo,
    by_type,
    key_metrics: metrics,
    filter,
  });
  const columns = [...defaultColumns, ...list].map(
    ({ name, key, format = reviewsFormat, ...other }) => {
      return {
        key,
        header: name,
        render: (cvm: any) => {
          const value = cvm[key];
          return format(value);
        },
        ...other,
      };
    },
  );

  return columns;
}

/**
 * @deprecated 请使用 @op/utils/chart 中的 getRealXAxisValue
 */
export function getRealXAxisValue({
  xAxis,
  dateType,
  format,
  xAxisData = [],
}: {
  xAxis: string;
  dateType: ParamsTimeType;
  format?: string;
  xAxisData: Array<string>;
}) {
  let realXAxisValue = xAxis;
  if (dateType === 'monthly') {
    realXAxisValue = moment(xAxis, 'YYYY-MM').format(format);
  } else if (dateType === 'hourly') {
    realXAxisValue = moment(xAxis, 'YYYY-MM-DD HH').format(format);
  } else if (dateType === 'daily') {
    realXAxisValue = moment(xAxis).format(format);
  } else if (dateType === 'weekly') {
    const xAxisMoment = moment(xAxis);
    for (const date of xAxisData || []) {
      const weekStart = moment(date);
      const weekEnd = weekStart.clone().add(6, 'days');

      if (xAxisMoment.isBetween(weekStart, weekEnd, 'day', '[]')) {
        const newTime = weekStart.format(format);
        realXAxisValue = format ? date : newTime;
        break;
      }
    }
  }

  return realXAxisValue;
}



export const MULTI_REGION_TITLE = {
  'zh-CN': '多区域异动',
  'en-US': 'Multi-region Events',
} as const;

export const TREND_TEXT = {
  up: { 'zh-CN': '情感分持续上升', 'en-US': 'Sentiment increase' },
  down: { 'zh-CN': '情感分持续下降', 'en-US': 'Sentiment decrease' },
  mixed: { 'zh-CN': '', 'en-US': '' },
} as const;

 /**
 * 根据标签名依次查找中英文映射
 * 优先级: region -> lang -> country
 */
export function getTagNameMap(tagName: string | undefined): {
  tagNameZh: string | undefined;
  tagNameEn: string | undefined;
} {
  if (!tagName) {
    return { tagNameZh: undefined, tagNameEn: undefined };
  }

  if (regionCn[tagName] || regionEn[tagName]) {
    return {
      tagNameZh: regionCn[tagName],
      tagNameEn: regionEn[tagName],
    };
  }

  if (langCn[tagName] || langEn[tagName]) {
    return {
      tagNameZh: langCn[tagName],
      tagNameEn: langEn[tagName],
    };
  }

  if (countryCn[tagName] || countryEn[tagName]) {
    return {
      tagNameZh: countryCn[tagName],
      tagNameEn: countryEn[tagName],
    };
  }

  return { tagNameZh: undefined, tagNameEn: undefined };
}